import {useCallback, useState, useEffect} from 'react';
import type {CSSProperties} from 'react';

import classnames from 'classnames';

import {colors} from '../colors/colors';
import {HtmlImageIcon} from '../icon/svg/html-image-icon';

import styles from './illustration.module.css';

interface IllustrationProps {
    src?: string;
    width?: number | string;
    height?: number | string;
    alt?: string;
    loading?: 'lazy' | 'eager';
    fullPath?: string;
}

const SLOW_LOAD_THRESHOLD_MS = 150;

export function isValidIllustrationPath(path: string | undefined): boolean {
    if (!path) {
        return false;
    }

    const trimmed = path.trim();
    if (!trimmed) {
        return false;
    }

    const upper = trimmed.toUpperCase();
    if (upper === 'TODO' || upper.startsWith('TODO ')) {
        return false;
    }

    return true;
}

type FallbackProps = {
    width?: number | string;
    height?: number | string;
};

function getFallbackIconSize(width?: number | string, height?: number | string): number {
    const numericWidth = typeof width === 'number' ? width : undefined;
    const numericHeight = typeof height === 'number' ? height : undefined;

    if (numericWidth && numericHeight) {
        return Math.round(Math.min(numericWidth, numericHeight) * 0.45);
    }
    if (numericWidth) {
        return Math.round(numericWidth * 0.45);
    }
    if (numericHeight) {
        return Math.round(numericHeight * 0.45);
    }

    return 28;
}

function IllustrationFallback(props: FallbackProps) {
    const styleObj: CSSProperties = {};

    if (props.width !== undefined) {
        styleObj.width = props.width;
    }
    if (props.height !== undefined) {
        styleObj.height = props.height;
    }
    if (props.width === undefined && props.height === undefined) {
        styleObj.width = 64;
        styleObj.height = 64;
    }

    return (
        <div className={styles.fallback} style={styleObj} aria-hidden={true}>
            <HtmlImageIcon
                size={getFallbackIconSize(props.width, props.height)}
                color={colors.greyLt}
                wrapWithDiv={false}
            />
        </div>
    );
}

export function Illustration(props: IllustrationProps) {
    const {src, width, height, alt = '', loading = 'lazy'} = props;

    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);

    // If image takes longer than SLOW_LOAD_THRESHOLD_MS to load (slow
    // connection), we want to animate it in for a smoother transition
    useEffect(() => {
        const timer = setTimeout(() => {
            setShouldAnimate(true);
        }, SLOW_LOAD_THRESHOLD_MS);

        return () => clearTimeout(timer);
    }, []);

    const handleLoad = useCallback(() => {
        requestAnimationFrame(() => {
            setIsLoaded(true);
        });
    }, []);

    const handleError = useCallback(() => {
        setHasError(true);
    }, []);

    const styleObj: CSSProperties = {};
    if (typeof width === 'string') {
        styleObj.width = width;
        if (!height) styleObj.height = 'auto';
    }

    if (typeof height === 'string') {
        styleObj.height = height;
        if (!width) styleObj.width = 'auto';
    }

    const illustrationSrc = props.fullPath
        ? props.fullPath
        : src
        ? `/include/illustrations/${src}.svg`
        : undefined;

    if (!isValidIllustrationPath(illustrationSrc) || hasError) {
        return <IllustrationFallback width={width} height={height} />;
    }

    return (
        <img
            src={illustrationSrc}
            width={width}
            height={height}
            alt={alt}
            loading={loading}
            style={styleObj}
            onLoad={handleLoad}
            onError={handleError}
            className={classnames({
                [styles['illustration-animate']]: shouldAnimate && !isLoaded,
                [styles['illustration-loaded']]: isLoaded,
            })}
        />
    );
}
